<template>
  <div class="container-wrapper" v-loading="loading">
    <div class="breadcrumb-wrapper">
      <span>{{ pageModeText }}</span>
      <el-button class="back" type="primary" @click="goPromotionList"
        >返回</el-button
      >
    </div>

    <div class="form-wrapper">
      <el-form
        style="min-width: 1200px"
        label-width="110px"
        label-position="left"
        :model="promoForm"
        ref="promoFormRef"
      >
        <!-- 活动名称 -->
        <el-form-item label="活动名称" required>
          <el-input
            v-model="promoForm.name"
            placeholder="请输入活动名称（最多20个字）"
            :maxlength="20"
            :disabled="isView"
            style="width: 360px"
          ></el-input>
          <span class="tip-text">（最多20个字，不允许重复）</span>
        </el-form-item>

        <!-- 促销时间 -->
        <el-form-item label="促销时间" required>
          <el-date-picker
            v-model="promoForm.timeRange"
            type="datetimerange"
            range-separator="至"
            placeholder="开始时间至结束时间"
            :disabled="isLocked"
            style="width: 420px"
          ></el-date-picker>
          <div class="tip-text">
            结束时间须 ≥ 开始时间；开始时间须 ≥ 当前系统时间
          </div>
        </el-form-item>

        <!-- 适用会员卡 -->
        <el-form-item label="适用会员卡" required>
          <el-select
            v-model="promoForm.cardId"
            placeholder="请选择已发布的售卖卡"
            :disabled="isLocked"
            style="width: 360px"
            @change="onCardChange"
          >
            <el-option
              v-for="card in cardOptions"
              :key="card.id"
              :label="`${card.name}（ID:${card.id}）`"
              :value="card.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <!-- 选中会员卡后展示的明细 -->
        <el-form-item v-if="selectedCard" label="卡片信息">
          <div class="card-info">
            <span>卡ID：{{ selectedCard.id }}</span>
            <span v-if="selectedCard.type === 3"
              >会员卡生效时间：{{
                $dayjs(selectedCard.effectiveStartTime).format("YYYY-MM-DD")
              }}
              至
              {{
                $dayjs(selectedCard.effectiveEndTime).format("YYYY-MM-DD")
              }}</span
            >
            <span v-else>会员卡时长：{{ selectedCard.duration }} 天</span>

            <span>是否自动续费：{{ autoRenewText }}</span>
            <span>售卖价格：{{ cardPriceText }}</span>
          </div>
        </el-form-item>

        <!-- 活动限制 -->
        <el-form-item label="活动限制">
          <el-select
            v-model="promoForm.limitType"
            :disabled="isLocked"
            style="width: 220px"
          >
            <el-option label="不限制" :value="1"></el-option>
            <el-option label="每个用户限购1次" :value="2"></el-option>
          </el-select>
        </el-form-item>

        <!-- 促销方案 -->
        <el-form-item label="促销方案" required>
          <div class="scheme-block">
            <el-button
              v-if="!isLocked && promoForm.schemes.length === 0"
              type="primary"
              size="small"
              @click="addScheme"
              >添加规则</el-button
            >

            <template v-else>
              <el-table :data="promoForm.schemes" border style="width: 100%">
                <el-table-column label="适用人群" width="120" align="center">
                  <template scope="{ row }">{{ row.crowdText }}</template>
                </el-table-column>
                <el-table-column prop="topTag" label="顶部标签" min-width="200">
                  <template scope="scope">
                    <el-input
                      v-model.lazy="scope.row.topTag"
                      placeholder="选填，最多6个字符"
                      :disabled="isView"
                      class="tag-input"
                      style="width: 180px"
                      @change="(e) => handleInput(e, 6, scope)"
                    >
                    </el-input>
                    <div
                      class="tip-text"
                      :class="{ tipsRed: scope.row['error-topTag'] }"
                    >
                      {{ topTagCount }} / 6
                    </div>
                  </template>
                </el-table-column>
                <el-table-column
                  prop="bottomTag"
                  label="底部标签"
                  min-width="240"
                >
                  <template scope="scope">
                    <el-radio-group
                      v-model="scope.row.bottomTagType"
                      :disabled="isView"
                      class="bottom-tag-type"
                    >
                      <el-radio :label="1">文字内容</el-radio>
                      <el-radio :label="2">活动倒计时</el-radio>
                    </el-radio-group>
                    <template v-if="scope.row.bottomTagType === 2">
                      <div class="fixed-tag-text">按促销时间显示倒计时</div>
                    </template>
                    <template v-else>
                      <el-input
                        v-model.lazy="scope.row.bottomTag"
                        placeholder="选填，最多8个字符"
                        :disabled="isView"
                        style="width: 180px"
                        @change="(e) => handleInput(e, 8, scope)"
                      ></el-input>
                      <div
                        class="tip-text"
                        :class="{ tipsRed: scope.row['error-bottomTag'] }"
                      >
                        {{ bottomTagCount }} / 8
                      </div>
                    </template>
                  </template>
                </el-table-column>
                <el-table-column label="促销价" min-width="280">
                  <template scope="{ row }">
                    <el-radio-group
                      v-model="row.priceType"
                      :disabled="isLocked"
                    >
                      <el-radio label="price">价格</el-radio>
                      <el-radio label="discount">折扣</el-radio>
                    </el-radio-group>
                    <div class="price-input-row">
                      <template v-if="row.priceType === 'price'">
                        <el-input-number
                          v-model="row.price"
                          :min="0.01"
                          :max="9999"
                          :step="0.01"
                          :disabled="isLocked"
                          style="width: 160px"
                        ></el-input-number>
                        <span class="tip-text"
                          >元（0.01~9999，最多2位小数）</span
                        >
                      </template>
                      <template v-else>
                        <el-input-number
                          v-model="row.discount"
                          :min="1"
                          :max="99"
                          :step="1"
                          :disabled="isLocked"
                          style="width: 120px"
                        ></el-input-number>
                        <span class="tip-text">%（1~99 整数）</span>
                        <span
                          v-if="computedDiscountPrice != null"
                          class="computed-price"
                          >促销价：¥{{ computedDiscountPrice }}</span
                        >
                      </template>
                    </div>
                  </template>
                </el-table-column>
                <!-- <el-table-column label="操作" width="90" align="center">
                  <template scope="{ row, $index }">
                    <el-button
                      v-if="!isView"
                      type="text"
                      style="color: #f56c6c"
                      @click="removeScheme($index)"
                      >删除</el-button
                    >
                  </template>
                </el-table-column> -->
              </el-table>
            </template>
          </div>
        </el-form-item>

        <!-- 活动说明 -->
        <el-form-item label="活动说明">
          <el-input
            v-model="promoForm.description"
            type="textarea"
            :rows="3"
            :disabled="isView"
            placeholder="请输入活动说明（选填）"
            style="width: 560px"
          ></el-input>
        </el-form-item>
      </el-form>
    </div>

    <div class="margin-20">
      <el-button v-if="!isView" type="primary" @click="clickPublish">{{
        isEdit ? "保存" : "发布"
      }}</el-button>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      loading: false,
      // ===== 会员卡促销创建表单（PRD） =====
      cardOptions: [], // 已发布售卖卡下拉 { id, name, duration, price(分), autoRenew }
      cardMap: {}, // id -> 卡片对象
      promoForm: this.getEmptyPromoForm(),
      promotionId: "", // 编辑模式下的活动 id
    };
  },

  computed: {
    // 本页即促销详情页，view / edit 模式由 actionType 决定（缺省为新增）
    isView() {
      return this.$route.query.actionType === "view";
    },
    // 编辑模式：仅活动名称、顶部标签、底部标签、活动说明可改
    isEdit() {
      return this.$route.query.actionType === "edit";
    },
    // 除可编辑项外的字段是否锁定（查看 / 编辑模式均锁定）
    isLocked() {
      return this.isView || this.isEdit;
    },
    pageModeText() {
      if (this.isView) return "会员卡促销 / 查看";
      if (this.isEdit) return "会员卡促销 / 编辑";
      return "会员卡促销 / 新增";
    },
    selectedCard() {
      return this.cardMap[this.promoForm.cardId] || null;
    },
    autoRenewText() {
      const c = this.selectedCard;
      if (!c) return "--";
      if (c.autoRenew === 1 || c.autoRenew === true) return "是";
      if (c.autoRenew === 0 || c.autoRenew === false) return "否";
      return "--";
    },
    cardPriceText() {
      const c = this.selectedCard;
      if (!c || c.price == null) return "--";
      return "¥" + (Number(c.price) / 100).toFixed(2);
    },
    topTagCount() {
      const s = this.promoForm.schemes[0];
      return s ? this.countLabelChars(s.topTag, 6).count : 0;
    },
    bottomTagCount() {
      const s = this.promoForm.schemes[0];
      return s ? this.countLabelChars(s.bottomTag, 8).count : 0;
    },
    computedDiscountPrice() {
      const s = this.promoForm.schemes[0];
      if (
        !s ||
        s.priceType !== "discount" ||
        !s.discount ||
        !this.selectedCard
      ) {
        return null;
      }
      const yuan = (this.selectedCard.price || 0) / 100;
      let p = Math.floor(((yuan * Number(s.discount)) / 100) * 100) / 100;
      if (p < 0.01) p = 0.01;
      return p.toFixed(2);
    },
  },

  created() {
    this.fetchCardOptions();
  },

  mounted() {
    const queryInfo = this.$route.query;
    // 查看 / 编辑：拉取活动详情回填表单
    if ((this.isView || this.isEdit) && queryInfo.id) {
      this.promotionId = Number(queryInfo.id);
      this.fetchDetail(Number(queryInfo.id));
    }
    if (queryInfo.copyId) {
      this.fetchCopy(Number(queryInfo.copyId));
    }
  },

  methods: {
    // ===================== 促销创建（PRD） =====================
    getEmptyPromoForm() {
      const now = new Date();
      return {
        name: "",
        timeRange: [now, null], // 开始时间默认当前系统时间
        cardId: "",
        limitType: 1, // 1=不限制（默认）
        description: "", // 活动说明（选填）
        // 默认预置一条空方案，进来即可直接编辑（最多一行）
        schemes: [
          {
            crowdText: "全部用户", // PRD：适用人群固定写死“全部用户”
            topTag: "",
            bottomTagType: 1, // 底部标签类型：1=文字内容 2=活动倒计时
            bottomTag: "",
            priceType: "price", // price | discount
            price: 0.01,
            discount: null,
          },
        ],
      };
    },

    // 适用会员卡下拉：sellList（无入参，仅返回已发布且 type 为售卖卡/预售卡的数据）
    fetchCardOptions() {
      this.$http({
        url: this.$util.domain + "/adminapi/privilegeConfig/sellList",
        method: "GET",
      })
        .then((res) => {
          const models = (res.data && res.data.data) || [];
          // 促销仅支持绑定售卖卡（type=1）
          const saleCards = models.filter((c) => c.type != 2);
          this.cardOptions = saleCards.map((c) => ({
            id: c.id,
            name: c.name,
            duration: c.duration,
            price: c.price,
            autoRenew: c.isAutoRenew, // 接口字段为 isAutoRenew
          }));
          this.cardMap = {};
          saleCards.forEach((c) => {
            this.$set(this.cardMap, c.id, { ...c, autoRenew: c.isAutoRenew });
          });
        })
        .catch(() => {
          this.cardOptions = [];
          this.cardMap = {};
        });
    },

    onCardChange() {
      // 切换会员卡后，折扣促销价需基于新卡价重算（computed 自动依赖 selectedCard）
    },

    // 查看模式：拉取活动详情回填表单（只读）
    fetchDetail(id) {
      this.loading = true;
      this.$http({
        url: this.$util.domain + "/adminapi/privilegePromotion/detail",
        method: "GET",
        params: { id },
      })
        .then((res) => {
          const data = (res.data && res.data.data) || {};
          this.fillPromoForm(data);
        })
        .catch(() => {
          this.$util.errorMsg("获取活动详情失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // 复制模式：拉取源活动详情，带入方案但清空活动名称、促销时间、适用会员卡（需用户重填/重选）
    fetchCopy(id) {
      this.loading = true;
      this.$http({
        url: this.$util.domain + "/adminapi/privilegePromotion/detail",
        method: "GET",
        params: { id },
      })
        .then((res) => {
          const data = (res.data && res.data.data) || {};
          this.fillPromoForm(data, true);
        })
        .catch(() => {
          this.$util.errorMsg("获取复制源活动详情失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    fillPromoForm(d, forCopy = false) {
      if (!d) return;
      if (d.id) {
        this.promotionId = d.id; // 编辑提交需要活动 id
      }
      const schemes = (d.schemes || []).map((s) => ({
        id: s.id, // 编辑提交需要方案 id
        crowdText:
          s.crowdRule ||
          (s.crowdType === 1
            ? "新用户"
            : s.crowdType === 2
            ? "老用户"
            : "全部用户"),
        topTag: s.topLabel || "",
        bottomTagType: s.bottomLabelType === 2 ? 2 : 1, // 未传默认 1（文案）
        bottomTag: s.bottomLabel || "",
        priceType: s.promotionType === 2 ? "discount" : "price",
        price: s.promotionType === 1 ? Number(s.promotionValue) || 0.01 : 0.01,
        discount:
          s.promotionType === 2 ? Number(s.promotionValue) || null : null,
      }));
      const empty = this.getEmptyPromoForm();
      this.promoForm = {
        name: forCopy ? "" : d.name || "",
        timeRange: forCopy
          ? empty.timeRange
          : [
              d.startTime ? new Date(d.startTime) : null,
              d.endTime ? new Date(d.endTime) : null,
            ],
        // 复制模式不带入适用会员卡（需用户重选），查看模式原样回填
        cardId: forCopy ? "" : d.privilegeConfigId || "",
        limitType: d.limitType === 2 ? 2 : 1,
        // 活动说明：复制模式清空（与活动名称一致，仅预填方案）
        description: forCopy ? "" : d.description || "",
        schemes: schemes.slice(0, 1),
      };
    },

    addScheme() {
      if (this.promoForm.schemes.length >= 1) return;
      this.promoForm.schemes.push({
        crowdText: "全部用户", // PRD：适用人群固定写死“全部用户”
        topTag: "",
        bottomTagType: 1, // 底部标签类型：1=文字内容 2=活动倒计时
        bottomTag: "",
        priceType: "price", // price | discount
        price: 0.01,
        discount: null,
      });
    },

    removeScheme(index) {
      // 无需二次确认
      this.promoForm.schemes.splice(index, 1);
    },

    /**
     * 计算自定义字符串长度，并在超出限制时截断
     * @param {string} str - 输入的字符串
     * @param {number} maxLength - 最大允许的长度
     * @returns {{ count: number, exceed: boolean, value: string }}
     */
    countLabelChars(str, maxLength) {
      let count = 0; // 真实总长度（按规则计算，未截断）
      let value = ""; // 截断后的有效值
      let currentLen = 0; // 当前已累加的有效长度
      let exceed = false; // 是否溢出

      // 使用 for...of 遍历，完美兼容 Emoji 等多码元字符
      for (let char of str) {
        let charCode = char.charCodeAt(0);
        // ASCII 码在 0-127 之间（包含英文、数字、常规符号）算 0.5，其他算 1
        let charLen = charCode >= 0 && charCode <= 127 ? 0.5 : 1;

        // 1. 无论是否溢出，都累加真实的总长度
        count += charLen;

        // 2. 只有在未溢出的情况下，才将字符拼接到有效值中
        if (!exceed) {
          if (currentLen + charLen <= maxLength) {
            value += char;
            currentLen += charLen;
          } else {
            // 一旦超出上限，标记 exceed 为 true，停止拼接有效值
            exceed = true;
          }
        }
      }

      return { count, exceed, value };
    },

    // 输入时实时截断：超出 limit 个字符时，用 countLabelChars 返回的合规 value 回写输入框与数据
    handleInput(e, limit, scoped) {
      const { row, $index, column } = scoped;
      const raw = e.target ? e.target.value : "";
      const res = this.countLabelChars(e, limit);
      console.log("handleInput", e, limit, scoped);
      row["error-" + column.property] = res.exceed;
      this.$set(row, "error-" + column.property, res.exceed);
      this.$forceUpdate();
      // if (!res.exceed) return;
      // if (e.target) e.target.value = res.value;
      this.$nextTick(() => {
        //   row[column.property] = res.value;
      });
    },

    formatDateTime(value) {
      if (!value) return "";
      const date = new Date(value);
      if (isNaN(date.getTime())) return "";
      const pad = (x) => String(x).padStart(2, "0");
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
        date.getDate()
      )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
        date.getSeconds()
      )}`;
    },

    goPromotionList() {
      this.$router.push({ path: "/yunying/user/member/promotion" });
    },

    validatePromo() {
      const f = this.promoForm;
      if (!f.name || !f.name.trim()) {
        this.$util.errorMsg("请输入活动名称");
        return false;
      }
      if (f.name.trim().length > 20) {
        this.$util.errorMsg("活动名称最多20个字");
        return false;
      }
      if (!f.timeRange || !f.timeRange[0] || !f.timeRange[1]) {
        this.$util.errorMsg("请选择促销时间");
        return false;
      }
      const start = new Date(f.timeRange[0]).getTime();
      const end = new Date(f.timeRange[1]).getTime();
      const now = Date.now();
      if (isNaN(start) || isNaN(end)) {
        this.$util.errorMsg("促销时间格式不正确");
        return false;
      }
      if (end < start) {
        this.$util.errorMsg("结束时间须 ≥ 开始时间");
        return false;
      }
      if (start < now) {
        this.$util.errorMsg("开始时间须 ≥ 当前系统时间");
        return false;
      }
      if (!f.cardId) {
        this.$util.errorMsg("请选择适用会员卡");
        return false;
      }
      if (!f.schemes.length) {
        this.$util.errorMsg("请添加促销方案");
        return false;
      }
      const s = f.schemes[0];
      if (this.countLabelChars(s.topTag, 6).exceed) {
        this.$util.errorMsg("顶部标签最多6个字符");
        return false;
      }
      // 活动倒计时模式下不校验底部标签文案
      if (
        s.bottomTagType !== 2 &&
        this.countLabelChars(s.bottomTag, 8).exceed
      ) {
        this.$util.errorMsg("底部标签最多8个字符");
        return false;
      }
      if (s.priceType === "price") {
        const p = Number(s.price);
        if (!(p >= 0.01 && p <= 9999)) {
          this.$util.errorMsg("促销价格需在 0.01~9999 之间");
          return false;
        }
        if (!/^\d+(\.\d{1,2})?$/.test(String(s.price))) {
          this.$util.errorMsg("促销价格最多2位小数");
          return false;
        }
      } else {
        const d = Number(s.discount);
        if (!(d >= 1 && d <= 99) || !Number.isInteger(d)) {
          this.$util.errorMsg("折扣需为 1~99 的整数");
          return false;
        }
      }
      return true;
    },

    // 编辑模式校验：只校验可改字段（活动名称、顶部标签、底部标签）
    validateEdit() {
      const f = this.promoForm;
      if (!f.name || !f.name.trim()) {
        this.$util.errorMsg("请输入活动名称");
        return false;
      }
      if (f.name.trim().length > 20) {
        this.$util.errorMsg("活动名称最多20个字");
        return false;
      }
      const s = f.schemes[0];
      if (!s) {
        this.$util.errorMsg("缺少促销方案");
        return false;
      }
      if (this.countLabelChars(s.topTag || "", 6).exceed) {
        this.$util.errorMsg("顶部标签最多6个字符");
        return false;
      }
      // 活动倒计时模式下不校验底部标签文案
      if (
        s.bottomTagType !== 2 &&
        this.countLabelChars(s.bottomTag || "", 8).exceed
      ) {
        this.$util.errorMsg("底部标签最多8个字符");
        return false;
      }
      return true;
    },

    // 编辑模式保存：仅提交活动名称、顶部标签、底部标签、活动说明
    clickSaveEdit() {
      if (!this.validateEdit()) return;
      const f = this.promoForm;
      const s = f.schemes[0] || {};
      const payload = {
        id: this.promotionId,
        name: f.name.trim(),
        description: (f.description || "").trim(), // 活动说明（选填）
        schemes: [
          {
            id: s.id, // 方案 id
            topLabel: s.topTag || "",
            // 底部标签类型 1-文案 2-倒计时；倒计时模式下不传底部标签内容
            bottomLabelType: s.bottomTagType === 2 ? 2 : 1,
            bottomLabel: s.bottomTagType === 2 ? "" : s.bottomTag || "",
          },
        ],
      };
      this.loading = true;
      this.$http({
        url: this.$util.domain + "/adminapi/privilegePromotion/update",
        method: "POST",
        body: JSON.stringify(payload),
      })
        .then((res) => {
          const resp = res.data || res.body;
          if (resp && resp.error === 10000) {
            this.$util.successMsg("保存成功");
            this.goPromotionList(); // 保存后回到会员卡促销列表
          } else {
            this.$util.errorMsg((resp && resp.message) || "保存失败");
          }
        })
        .catch((err) => {
          this.$util.errorMsg((err && err.message) || "保存失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    clickPublish() {
      // 编辑模式走 update（仅名称与标签）
      if (this.isEdit) {
        this.clickSaveEdit();
        return;
      }
      if (!this.validatePromo()) return;
      const f = this.promoForm;
      const s = f.schemes[0];
      const scheme = {
        crowdType: 0, // PRD：适用人群固定“全部用户”；后端 crowdType 仅 1/2，暂送 1
        crowdRule: "全部用户",
        topLabel: s.topTag || "",
        // 底部标签类型 1-文案 2-倒计时；倒计时模式下不传底部标签内容
        bottomLabelType: s.bottomTagType === 2 ? 2 : 1,
        bottomLabel: s.bottomTagType === 2 ? "" : s.bottomTag || "",
        promotionType: s.priceType === "discount" ? 2 : 1,
        promotionValue:
          s.priceType === "discount" ? Number(s.discount) : Number(s.price),
      };
      const payload = {
        name: f.name.trim(),
        startTime: new Date(f.timeRange[0]).getTime(),
        endTime: new Date(f.timeRange[1]).getTime(),
        privilegeConfigId: f.cardId,
        limitType: f.limitType,
        description: (f.description || "").trim(), // 活动说明（选填）
        schemes: [scheme],
      };
      this.loading = true;
      this.$http({
        url: this.$util.domain + "/adminapi/privilegePromotion/add",
        method: "POST",
        body: JSON.stringify(payload),
      })
        .then((res) => {
          const resp = res.data || res.body;
          if (resp && resp.error === 10000) {
            this.$util.successMsg("发布成功");
            this.goPromotionList(); // 发布后回到会员卡促销列表
          } else {
            this.$util.errorMsg((resp && resp.message) || "发布失败");
          }
        })
        .catch((err) => {
          this.$util.errorMsg((err && err.message) || "发布失败");
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.form-wrapper {
  min-width: 1200px;
}
.breadcrumb-wrapper {
  margin-bottom: 12px;
}
.tip-text {
  margin-left: 8px;
  color: #909399;
  font-size: 12px;
}

.tipsRed {
  color: #f00;
}
.card-info {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 24px;
  padding: 10px 14px;
  background: #f5f7fa;
  border-radius: 4px;
  color: #606266;
  font-size: 13px;
}
.scheme-block {
  width: 100%;
}
.price-input-row {
  margin-top: 6px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.computed-price {
  color: #f56c6c;
  font-weight: 600;
}
.bottom-tag-type {
  margin-bottom: 6px;
}
.bottom-tag-type .el-radio + .el-radio {
  margin-left: 16px;
}
.fixed-tag-text {
  height: 60px;
  line-height: 36px;
  color: #909399;
  font-size: 13px;
}

.tag-input > .el-input__inner {
  border-right: none;
}
</style>



// WEBPACK FOOTER //
// MemberPromotionDetail.vue?1271cb66